/**
 * Pilha das páginas visitadas na aba (ou janela do PWA), na mesma ordem do histórico do navegador
 * — base do "Voltar" que retorna para a origem com os filtros (a URL completa, query incluída).
 * Pura (sem Router) pra ser testável; quem liga no Router é NavigationHistoryService.
 */

/** Como a navegação mexeu no histórico: nova entrada, troca da atual ou voltar/avançar do navegador. */
export type NavigationKind = 'push' | 'replace' | 'popstate';

/** Caminho sem query string nem fragmento: "/a/b?tab=x#y" → "/a/b". */
export function pathOf(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}

/** Segmentos de rota das telas de criar/editar (contrato, ajuste, cluster, álbum, pesquisa, permissões). */
const FORM_SEGMENTS = new Set([
  'new', 'edit', 'create', 'contract-form', 'ajustes-form', 'cluster-create', 'cluster-edit',
  'roles-permissions-form', 'permission-form',
]);

/**
 * Tela de formulário (criar/editar): nunca é destino do "Voltar". Salvar costuma levar ao detalhe
 * do registro, e voltar dali pro formulário reabriria um cadastro já concluído.
 */
export function isFormPath(path: string): boolean {
  return pathOf(path).split('/').some((segment) => FORM_SEGMENTS.has(segment));
}

export class NavigationHistoryStack {
  private readonly entries: string[];
  /**
   * Quantas entradas do início da pilha não são contíguas ao histórico do navegador: vieram de
   * uma carga anterior do app (link externo na mesma aba, PWA reaberto) e, entre elas e a atual,
   * o histórico pode ter páginas de fora — voltar pelo histórico até elas poderia sair do app.
   */
  private boundary = 0;

  constructor(entries: ReadonlyArray<string> = [], private readonly maxEntries = 50) {
    this.entries = entries.slice(-maxEntries);
  }

  /**
   * App carregado do zero (não F5 nem voltar/avançar do navegador): o que já está na pilha deixa
   * de ser contíguo ao histórico. Chamado antes da primeira navegação da carga.
   */
  public markNewLoad(): void {
    this.boundary = this.entries.length;
  }

  public get urls(): ReadonlyArray<string> {
    return this.entries;
  }

  public record(url: string, kind: NavigationKind): void {
    const last = this.entries.length - 1;

    if (kind === 'replace' && last >= 0) {
      this.entries[last] = url;
      return;
    }

    if (kind === 'popstate') {
      // Voltar do navegador (um ou mais passos): a pilha volta até a entrada dessa URL.
      for (let i = last - 1; i >= 0; i--) {
        if (this.entries[i] === url) {
          this.entries.length = i + 1;
          return;
        }
      }
      // Avançar, ou entrada que a pilha não conhece: segue como uma nova.
    }

    // Recarregar a página (ou navegar pra mesma URL) não cria entrada no histórico.
    if (last >= 0 && this.entries[last] === url) return;

    this.entries.push(url);
    if (this.entries.length > this.maxEntries) {
      const removed = this.entries.length - this.maxEntries;
      this.entries.splice(0, removed);
      this.boundary = Math.max(0, this.boundary - removed);
    }
  }

  /**
   * Para onde o "Voltar" leva: a última página DIFERENTE da atual — trocar de aba ou de filtro na
   * mesma página não conta como página anterior — que não seja excluída (ex.: telas de login).
   * `steps` é quantas entradas do histórico separam a atual dela; `inHistory` diz se dá pra chegar
   * nela pelo próprio histórico do navegador (é desta carga do app). Null quando não há origem
   * (aberto direto por link, notificação ou no PWA recém-aberto): aí vale o destino fixo.
   */
  public backTarget(
    isExcluded: (path: string) => boolean = () => false,
  ): { url: string; steps: number; inHistory: boolean } | null {
    const last = this.entries.length - 1;
    if (last < 1) return null;

    const currentPath = pathOf(this.entries[last]);

    for (let i = last - 1; i >= 0; i--) {
      const path = pathOf(this.entries[i]);
      if (path !== currentPath && !isExcluded(path)) {
        return { url: this.entries[i], steps: last - i, inHistory: i >= this.boundary };
      }
    }

    return null;
  }
}
